import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { api, constants, dateHelpers } from "../../utils";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import _ from "lodash";
import MachineConstants from "./MachineSetupConstants";
import {
   Machine861FC,
   Machine861Tack,
   Machine861Wax,
   MachineDoubler,
   MachineDP5,
   MachineDS10E,
   MachineDS21,
   MachineDuro,
   MachineHacoba,
   MachineHeliKV2019B,
   MachineHeliPirn,
   MachineHeliTwister,
   MachineLeesonaM50,
   MachineLeesonaM50Wax,
   MachineMahanDryer,
   MachineMahanOven,
   MachineRatera,
   MachineSacoRT,
   MachineSahm,
   MachineSaurerTC2,
   MachineSSMKingspool,
   MachineTaining35AFP,
   MachineTaining35AR,
   MachineTaining35ATube,
   MachineTainingS41,
   MachineTexKingSpool,
   MachineUT50,
   MachineXeno,
   MachineXorella,
} from "./index";

const { AllErrors, DefaultErrors, LookUpValidations, DefaultMachineSetup } = MachineConstants;

// creating context
const MachineSetupContextState = createContext();
const MachineSetupContextUpdater = createContext();
const MachineSetupContextLoading = createContext();
const ALL_OPTION = { value: 0, label: "ALL", setupId: 0, num: 0, side: "" };
const MACH_MODEL = { setupId: 0, machineNum: 0, side: "" };

const MachineSetupContextProvider = ({ children }) => {
   const { machineSetupSelect } = useParams();

   // items on context
   const [machineSetup, setMachineSetup] = useState(DefaultMachineSetup);
   const [selectedMachineSetup, setSelectedMachineSetup] = useState(null);
   const [selectedMachines, setSelectedMachines] = useState(null); // List of valid machine numbers for this item
   const [specification, setSpecification] = useState(null);
   const [selectedItem, setSelectedItem] = useState(null);
   const [itemToCopy, setItemToCopy] = useState(null);
   const [itemToDelete, setItemToDelete] = useState(null);
   const [errorDivDefinitions, setErrorDivDefinitions] = useState(DefaultErrors);
   const [machineNums, setMachineNums] = useState(null); // List of active machine numbers for selected setup
   const [setupId, setSetupId] = useState(0);

   // bools and misc
   const [isLoading, setIsLoading] = useState(false);
   const [copyToItemOptions, setCopyToItemOptions] = useState([]);
   const [printOptions, setPrintOptions] = useState({
      printRevisionNotes: false,
      printConfiguration: false,
   });

   // lists
   const [machineTypes, setMachineTypes] = useState([]);
   const [calcTypes, setCalcTypes] = useState([]); // ["Sole Setup", "Cumulative Setup", "Alternate Setup", "Unknown"]

   // ref
   const MachineSetupDisplay = {
      1: <Machine861FC />,
      5: <Machine861Tack />,
      6: <Machine861Wax />,
      7: <MachineDoubler />,
      21: <MachineDP5 />,
      32: <MachineDS10E />,
      2: <MachineDS21 />,
      38: <MachineDuro />,
      20: <MachineHacoba />,
      35: <MachineHeliKV2019B />,
      29: <MachineHeliPirn />,
      30: <MachineHeliTwister />,
      3: <MachineLeesonaM50 />,
      16: <MachineLeesonaM50Wax />,
      39: <MachineMahanDryer />,
      27: <MachineMahanOven />,
      19: <MachineRatera />,
      14: <MachineSacoRT />,
      13: <MachineSahm />,
      10: <MachineSaurerTC2 />,
      31: <MachineSSMKingspool />,
      11: <MachineTaining35AFP />,
      12: <MachineTaining35AR />,
      22: <MachineTaining35ATube />,
      28: <MachineTainingS41 />,
      9: <MachineTexKingSpool />,
      15: <MachineUT50 />,
      36: <MachineXeno />,
      37: <MachineXorella />,
   };

   // Updaters
   // eslint-disable-next-line react-hooks/exhaustive-deps
   const payloadValidity = useCallback((machineSetupParam = machineSetup) => {
      const totalValidations = LookUpValidations[machineSetupParam.machineType];
      const extProperties = machineSetupParam.extendedPropertyJson;
      if (_.isNil(totalValidations)) {
         toast.warning("Something went wrong while attemping to check validations");
         return;
      }

      let validations = _.cloneDeep(errorDivDefinitions);
      let LimitedValidationKeys = _.map(totalValidations, (index) => AllErrors[index]);
      let filteredValidations = _.pick(validations, LimitedValidationKeys);
      if (_.isNil(filteredValidations) || filteredValidations === []) {
         toast.error("Something went wrong while attempting to validate form");
         return;
      }

      validations = filteredValidations;
      for (let index = 0; index < totalValidations.length; index++) {
         const propertyName = AllErrors[totalValidations[index]];
         if (_.isNil(extProperties[propertyName]) || extProperties[propertyName] === "") {
            if (_.includes(["gearSpindle", "gearIntermediate", "gearInnerGainer", "gearOuterGainer", "camGear",], propertyName)) {
               // Check if gearDriven is true
               if (extProperties["gearDriven"] === true || extProperties["gearDriven"] === "true") {
                  // no strict check in case its a string "true"
                  validations[propertyName] = true;
               }
            } else if (propertyName === "bullet") {
               if (extProperties["tensionDevice"] === "Yarn Tensioner") {
                  validations[propertyName] = true;
               } else {
                  validations[propertyName] = false;
               }
            } else if (propertyName === "tensionSet"
               && machineSetup.machineType === 15) {          // UT50-60
               if (extProperties["tensionDevice"] === "Yarn Tensioner") {
                  validations[propertyName] = true;
               } else {
                  validations[propertyName] = false;
               }
            } else if (_.includes(["windRatioStart", "windRatioEnd"], propertyName)
               && (
                  selectedMachineSetup.machineType === 1 ||   // 861 FC
                  selectedMachineSetup.machineType === 5 ||   // 861 Tack
                  selectedMachineSetup.machineType === 6 ||   // 861 Wax
                  selectedMachineSetup.machineType === 32     // DS10E
               )
            ) {
               validations[propertyName] = true;
            } else if (_.includes(["degreesStart", "degreesEnd", "windRatioStart", "windRatioEnd"], propertyName)) {
               if (extProperties["windType"] === "Digiwind" && _.includes(["degreesStart", "degreesEnd"], propertyName)) {
                  validations[propertyName] = true;
                  validations.windRatioStart = false;
                  validations.windRatioEnd = false;
               } else if (extProperties["windType"] === "Precision" && _.includes(["windRatioStart", "windRatioEnd"], propertyName)) {
                  validations[propertyName] = true;
                  validations.degreesStart = false;
                  validations.degreesEnd = false;
               }
            } else if (_.includes(["dancerArmTension", "recipe"], propertyName)
               && selectedMachineSetup.machineType === 27     // Mahan Oven
            ) {
               validations[propertyName] = false;
            } else if (
               _.includes(
                  [
                     "creelTensionMin",
                     "creelTensionMax",
                     "zoneBBarPos",
                     "zoneBTensionMax",
                     "zoneBTensionMin",
                     "zoneCTensionMax",
                     "zoneCTensionMin",
                  ],
                  propertyName
               ) && machineSetup.machineType === 27           // Mahan Oven
            ) {
               if (extProperties["creelType"] === "Standard") {
                  validations[propertyName] = true;
               } else if (extProperties["creelType"] === "Roll Off") {
                  validations.creelTensionMin = false;
                  validations.creelTensionMax = false;
                  validations.zoneBBarPos = false;
                  validations.zoneBTensionMax = false;
                  validations.zoneBTensionMin = false;
                  validations.zoneCTensionMax = false;
                  validations.zoneCTensionMin = false;
               }
            } else if (_.includes(["springNum"], propertyName)) {
               if (extProperties["tensionDevice"] === "Flyer") {
                  validations.springNum = false;
               }
            } else if (propertyName === "program"
               && machineSetup.machineType === 7) {           // Gilbos Doubler
               validations.program = false;
            } else {
               if (_.includes(propertyName, "traveler") && _.includes(propertyName, "Qty")) {
                  // Extract traveler number from propertyName
                  let travelerNumber = propertyName
                     .replace("traveler", "")
                     .replace("Qty", "");

                  // Check if the corresponding traveler boolean is true
                  if (extProperties[`traveler${travelerNumber}`] === true) {
                     validations[propertyName] = true;
                  }
               } else {
                  if (_.includes(propertyName, "traveler") && !_.includes(propertyName, "Qty"))
                     continue;
                  validations[propertyName] = true;
               }
            }
         }
      }

      if (_.some(validations, (x) => x === true)) {
         setErrorDivDefinitions(validations);
         return false;
      }
      return true;
   });

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const handleSaveMachineSetup = useCallback((payload = machineSetup) => {
      if (machineSetup?.calcType === "C") {
         if (isNaN(machineSetup?.orderToProcess)) {
            toast.warning("Please enter a number for Order To Process.");
            return;
         }
      }

      // check that all the values pass the onChange Check else abort
      if (payloadValidity(payload)) {
         toast.success("Saving your machine setup...!");
      } else {
         toast.warning(`Please correct the fields that are highlighted in red, then try again. Check Radio Buttons as well.`);
         return;
      }

      function toPascalCase(str) {
         return _.upperFirst(_.camelCase(str));
      }

      const updatedPayload = {
         ...payload,
         extendedPropertyJson: _.mapKeys(
            payload.extendedPropertyJson,
            (_value, key) => toPascalCase(key)
         ),
      };

      //naming quirks
      if (updatedPayload.machineType === 2 ||
         updatedPayload.machineType === 5 ||
         updatedPayload.machineType === 13) {
         updatedPayload.extendedPropertyJson.ApplicatorRollRPM = updatedPayload.extendedPropertyJson?.ApplicatorRollRpm;
      }

      if (updatedPayload.machineType === 6) {
         updatedPayload.extendedPropertyJson.CounterFC71 = updatedPayload.extendedPropertyJson?.CounterFc71;
         updatedPayload.extendedPropertyJson.CounterFC72 = updatedPayload.extendedPropertyJson?.CounterFc72;
      }

      if (
         updatedPayload.machineType === 21 ||
         updatedPayload.machineType === 36 ||
         updatedPayload.machineType === 38
      ) {
         updatedPayload.extendedPropertyJson.TubeOD = updatedPayload.extendedPropertyJson?.TubeOd;
      }

      if (
         updatedPayload.machineType === 11 ||
         updatedPayload.machineType === 12 ||
         updatedPayload.machineType === 22
      ) {
         updatedPayload.extendedPropertyJson.TensionLI = updatedPayload.extendedPropertyJson?.TensionLi;
      }

      setIsLoading(true);
      let url = `MachineSetup/SaveMachineSetup`;
      // let url = setNewItemToSelected ? 'MachineSetup/CopyMachineSetup' : `MachineSetup/SaveMachineSetup`;
      api
         .post(url, updatedPayload)
         .then((res) => {
            if (res.data.success) {
               toast.success("Saved successfully");
               setSelectedItem({ ...selectedItem });
               setItemToCopy(null);
               setSelectedMachineSetup(null);
               // if (setNewItemToSelected) {
               //   setSelectedItem(setNewItemToSelected)
               // }
            } else if (res.data.message) {
               toast.warning(res.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setIsLoading(false));
   });

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const onChange = useCallback((propertyName, value, ext = false) => {
      if (ext) propertyName = `extendedPropertyJson.${propertyName}`;
      let copy = _.cloneDeep(machineSetup);
      _.set(copy, propertyName, value);
      setMachineSetup(copy);
   });

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const onChangeErrorDivDefinitions = useCallback((propertyName, value) => {
      setErrorDivDefinitions({
         ...errorDivDefinitions,
         [propertyName]: value,
      });
   });

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const onChangeSelectedMachine = useCallback((options) => {
      let arr = [];
      if (options && options.length > 0) {
         var thisOpt = options[options.length - 1];
         if (thisOpt.value === 0) {
            // All option clears everything else
            setSelectedMachines([thisOpt]);
            let thisMach = Object.assign({}, MACH_MODEL);
            thisMach.setupId = setupId;
            thisMach.machineNum = thisOpt.num;
            thisMach.side = thisOpt.side;
            arr.push(thisMach);
         }
         else {
            // Any other option must exclude the All option
            options = options.filter(item => item.value > 0);
            setSelectedMachines(options);
            for (let x = 0; x < options.length; x++) {
               let xMach = Object.assign({}, MACH_MODEL);
               xMach.setupId = options[x].setupId;
               xMach.machineNum = options[x].num;
               xMach.side = options[x].side;
               arr.push(xMach);
            }
         }
      } else {
         // There should always be at least the default all option
         let dfltOpt = Object.assign({}, ALL_OPTION);
         dfltOpt.setupId = setupId;
         setSelectedMachines([dfltOpt]);
         let dfltMach = Object.assign({}, MACH_MODEL);
         dfltMach.setupId = setupId;
         arr.push(dfltMach);
      }
      onChange("validMachines", arr);
      //console.log(arr);
   });

   function fixMachines(activeMachines, validMachines, id) {
      // Set up the default option
      setSetupId(id);
      let dfltMach = Object.assign({}, ALL_OPTION);
      dfltMach.setupId = id;

      // Fill in the valid machines drop-down with active machines for the selected machine setup
      var nums = activeMachines.length > 0 ? activeMachines.split(", ") : "";
      if (nums.length > 0) {
         let newMachineList = [dfltMach];
         let validList = null;
         let tmpIdx = 1;
         for (let ix = 0; ix < nums.length; ix++) {
            if (selectedMachineSetup.hasBSide) {
               let machItm = _.find(validMachines, { machineNum: parseInt(nums[ix], 10), side: 'A' });
               validList = machItm ? _.concat(validList, ({ value: tmpIdx, label: `${nums[ix]}A`, setupId: id, num: parseInt(nums[ix], 10), side: "A" })) : validList;
               newMachineList = _.concat(newMachineList, ({ value: tmpIdx, label: `${nums[ix]}A`, setupId: id, num: parseInt(nums[ix], 10), side: "A" }));
               tmpIdx++;

               let machItm2 = _.find(validMachines, { machineNum: parseInt(nums[ix], 10), side: 'B' });
               validList = machItm2 ? _.concat(validList, ({ value: tmpIdx, label: `${nums[ix]}B`, setupId: id, num: parseInt(nums[ix], 10), side: "B" })) : validList;
               newMachineList = _.concat(newMachineList, ({ value: tmpIdx, label: `${nums[ix]}B`, setupId: id, num: parseInt(nums[ix], 10), side: "B" }));
               tmpIdx++;
            }
            else {
               let machItm = _.find(validMachines, { machineNum: parseInt(nums[ix], 10), side: '' });
               validList = machItm ? _.concat(validList, ({ value: tmpIdx, label: `${nums[ix]}`, setupId: id, num: parseInt(nums[ix], 10), side: "" })) : validList;
               newMachineList = _.concat(newMachineList, ({ value: tmpIdx, label: `${nums[ix]}`, setupId: id, num: parseInt(nums[ix], 10), side: "" }));
               tmpIdx++;
            }
         }
         setMachineNums(newMachineList);
         setSelectedMachines(validList ? validList : [dfltMach]);
      }
      else {
         setMachineNums([dfltMach]);
         setSelectedMachines([dfltMach]);
      }
   };

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const handleInputRangeChange = useCallback(
      (event, min, max, property, string = false, float = false, reqd = true) => {
         const { value } = event.target;
         if (string) {
            onChange(property, value, true);
            onChangeErrorDivDefinitions(property, false);
            return;
         }
         if (value === "") {
            onChange(property, value, true);
            if (reqd)
               onChangeErrorDivDefinitions(property, true);
            else
               onChangeErrorDivDefinitions(property, false);
            return;
         }

         const regex = /^[+-]?|[+-]?([0-9]+(\.[0-9]*)?|\.[0-9])$/i;
         const found = value.match(regex);
         if (found === null) {
            toast.warning(`your number ${value} is not formatted correctly please try again`);
            return;
         }

         const parsedValue = float ? parseFloat(value) : parseInt(value);
         if (float) { // Allow decimals, save actual value
            if (parsedValue >= min && parsedValue <= max) {
               onChange(property, value, true);
               if (errorDivDefinitions[property]) {
                  onChangeErrorDivDefinitions(property, false);
               }
            } else {
               onChange(property, value, true);
               onChangeErrorDivDefinitions(property, true);
            }
         }
         else { // Do not allow decimals, save parsedValue
            if (parsedValue >= min && parsedValue <= max) {
               onChange(property, parsedValue, true);
               if (errorDivDefinitions[property]) {
                  onChangeErrorDivDefinitions(property, false);
               }
            } else {
               onChange(property, parsedValue, true);
               onChangeErrorDivDefinitions(property, true);
            }
         }
      });

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const handleInputRangeChangeFloatIncremental = useCallback(
      (event, min, max, property, increment, reqd = true) => {
         const { value } = event.target;
         if (value === "") {
            onChange(property, value, true);
            if (reqd)
               onChangeErrorDivDefinitions(property, true);
            else
               onChangeErrorDivDefinitions(property, false);
            return;
         }

         const regex = /^[+-]?|[+-]?([0-9]+(\.[0-9]*)?|\.[0-9])$/i;
         const found = value.match(regex);
         if (found === null) {
            toast.warning(`your number ${value} is not formatted correctly please try again`);
            return;
         }

         const parsedValue = parseFloat(value);
         let isGood = (parsedValue % increment === 0);
         if (!isGood) {
            toast.warning(`your number ${value} must be in increments of ${increment} please try again`);
            return;
         }

         if (parsedValue >= min && parsedValue <= max) {
            onChange(property, value, true);
            if (errorDivDefinitions[property]) {
               onChangeErrorDivDefinitions(property, false);
            }
         } else {
            onChange(property, value, true);
            onChangeErrorDivDefinitions(property, true);
         }
      });

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const handleInputTimeChange = useCallback((event, propertyName) => {
      const { value } = event.target;
      const regex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])$/;
      if (regex.test(value)) {
         onChange(propertyName, value, true);
         onChangeErrorDivDefinitions(propertyName, false);
      } else {
         onChange(propertyName, value, true);
         onChangeErrorDivDefinitions(propertyName, true);
      }
   });

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const handleItemSelectChange = useCallback((item) => {
      if (item === null) {
         setSelectedItem(null);
         return;
      }

      if (typeof item?.value !== "string" || item?.value === "") {
         toast.error("Invalid item selected");
         return;
      }
      setSelectedItem(item);
   });

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const newHandleNewSetup = useCallback((item = {}) => {
      const setupsAreNonExistent = !specification?.machineSetupList?.length;
      const activeSetups = _.chain(specification?.machineSetupList)
         .filter({ isActive: true })
         .toArray()
         .value();

      if (!specification || !specification.itemNumber) {
         toast.error("Specification cannot be null when saving a new setup. Please contact an admin.");
         return;
      }

      let newMachine = _.assign({}, DefaultMachineSetup);
      newMachine.itemNumber = specification.itemNumber;
      if (setupsAreNonExistent) {
         newMachine.calcType = constants.CALCULATION_TYPES.SOLE_SETUP.value;
      } else {
         if (_.isEmpty(activeSetups)) {
            newMachine.calcType = constants.CALCULATION_TYPES.SOLE_SETUP.value;
         } else if (_.size(activeSetups) === 1) {
            // This is going to be the 2nd active setup to be created. The calc type for a setup group is not known yet.
            // Default to "alternate" type and let the user pick one on edit.
            newMachine.calcType = constants.CALCULATION_TYPES.ALTERNATE.value;
         } else {
            //Two or more active setups already exist, so make this one the same calc type as the others.
            newMachine.calcType = activeSetups[0].calcType;
         }
      }

      try {
         // old code from legacy
         setIsLoading(true);
         api
            .post("MachineSetup/SaveMachineSetup", newMachine)
            .then((response) => {
               if (response.data.success) {
                  toast.success("Saved successfully");
                  newMachine.setupId = response.data.machineSetupId;
                  setMachineSetup(newMachine);
               } else if (response.data.message) {
                  toast.error(response.data.message);
               }
            })
            .catch(api.catchHandler)
            .finally(() => {
               setIsLoading(false);
               setSelectedItem({ ...selectedItem });
            });
         // SetSpecification() // but we already have the specification for this item
         // LoadMachineSetups() // refresh the screen and pull the data again
         // LoadedSetup = newMachine // we set the machineSetup on state
         // LoadChildControl(newMachine) // not sure what this is yet
      } catch (error) {
         console.error(error);
         toast.error("Error while saving new setup. Please contact an admin.");
      }
   });

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const handldePrintActiveSetups = useCallback(() => {
      setIsLoading(true);
      let payload = _.assign({}, DefaultMachineSetup);
      payload.PrintRevisionNotes = printOptions.printRevisionNotes;
      payload.PrintConfigFields = printOptions.printConfiguration;
      payload.ItemNumber = specification.itemNumber;
      api
         .postDownloadBinary(
            "Report/ActiveMachineSetupsReport",
            payload,
            constants.MIME_TYPES.pdf,
            `MSR_${specification.itemNumber.trim()}_${dateHelpers.getDateStringForReportName()}.pdf`,
            "There was an error downloading the active machine setups report"
         )
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setIsLoading(false);
         });
   });

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const handlePrintMachineSetup = useCallback((index) => {
      let newList = specification.machineSetupList.slice();
      let itemToPrint = _.assign({}, newList[index]);
      setIsLoading(true);
      let payload = {
         ItemNumber: itemToPrint.itemNumber,
      };
      payload.PrintRevisionNotes = printOptions.printRevisionNotes;
      payload.PrintConfigFields = printOptions.printConfiguration;
      payload.SetUpId = _.isNil(itemToPrint.setupId)
         ? ""
         : _.toString(itemToPrint.setupId);
      api
         .postDownloadBinary(
            "Report/MachineSetupReport",
            payload,
            constants.MIME_TYPES.pdf,
            `MSR_${itemToPrint.itemNumber.trim()}_${dateHelpers.getDateStringForReportName()}.pdf`,
            "There was an error downloading the machine setup report"
         )
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setIsLoading(false));
   });

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const handleDeactivateMachineSetup = useCallback((item = {}) => {
      if (_.isEmpty(item)) {
         toast.error("There was an error deactivating the machine setup, item to deactivate not found");
         return;
      }
      setIsLoading(true);
      api
         .fetch(`MachineSetup/ToggleActiveMachineSetup`, {
            SetupId: item.setupId,
            ItemNumber: item.itemNumber,
         })
         .then((res) => {
            if (res.data.success) {
               toast.success("Successfully updated the setup");
               setSelectedItem({ ...selectedItem });
            } else {
               toast.warning(
                  res?.data?.message ||
                  "There was an error deactivating or activating the machine setup"
               );
            }
         })
         .catch(api.catchHandler)
         .finally(() => setIsLoading(false));
   });

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const handleDeleteMachineSetup = useCallback((item = itemToDelete) => {
      if (!itemToDelete) {
         toast.error("There was an error deleting the machine setup, item to delete not found");
         return;
      }
      setIsLoading(true);
      api
         .fetch(`MachineSetup/DeleteMachineSetup`, {
            SetupId: item.setupId,
            ItemNumber: item.itemNumber,
         })
         .then((res) => {
            if (res && res.data && res.data.success) {
               setItemToDelete(null);
               setSelectedItem({ ...selectedItem });
               toast.success("Machine Setup successfully deleted");
            } else {
               toast.error(
                  res?.data?.message ||
                  "There was an error deleting the machine setup"
               );
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setIsLoading(false);
            setItemToDelete(null);
            setSelectedMachineSetup(null);
            setMachineSetup(DefaultMachineSetup);
         });
   });

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const handleCopyMachineItemSetup = useCallback(() => {
      if (!itemToCopy) {
         toast.error("There was an error copying the machine setup, item to copy not found");
         return;
      }
      setIsLoading(true);
      api
         .fetch(`MachineSetup/CheckForDuplicateMachineSetup`, {
            ItemNumber: itemToCopy.itemNumber || itemToCopy.value,
            MachineType: itemToCopy.machineType,
         })
         .then((res) => {
            if (res?.data?.alreadyExists) {
               toast.error(`This machine type already exists on item number ${itemToCopy.itemNumber}. Please cancel and go to that item number to make changes.`);
            } else {
               let machineSetup = _.assign({}, DefaultMachineSetup);
               //assign the properties from that other machine
               machineSetup.extendedPropertyJson = _.assign({}, itemToCopy.extendedPropertyJson);
               machineSetup.itemNumber = itemToCopy.itemNumber;
               machineSetup.calcType = itemToCopy.calcType;
               machineSetup.machineType = itemToCopy.machineType;
               machineSetup.setupId = 0;
               handleSaveMachineSetup(machineSetup);
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setIsLoading(false);
            setItemToCopy(null);
         });
   });

   // effects
   useEffect(() => {
      if (selectedMachineSetup === null) {
         setMachineSetup(null);
         setSetupId(0);
         fixMachines("", "", 0);
         setErrorDivDefinitions(DefaultErrors);
         return;
      } else {
         let extProperties;
         setSetupId(selectedMachineSetup.setupId);
         fixMachines(selectedMachineSetup.activeMachines, selectedMachineSetup.validMachines, selectedMachineSetup.setupId);

         if (selectedMachineSetup.reset) {
            extProperties = {
               stdEfficiency: selectedMachineSetup.defaultEfficiency || "",
               stdSpindleCount: selectedMachineSetup.defaultSpindles || "",
               laborPct: "100",
            };
            // constant values for certain machine types
            if (selectedMachineSetup.machineType === 39)   // Mahan Dryer
               _.assign(extProperties, { pounds: 2000 });  // constant value

            if (selectedMachineSetup.machineType === 29)   // Heli KV2002
               _.assign(extProperties, { girth: 0.0503, type: 1 }); // constant values

            if (
               selectedMachineSetup.machineType === 16 ||  // M50 Wax
               selectedMachineSetup.machineType === 3      // M50
            )
               _.assign(extProperties, { scaleFactor: 0.09243 }); // constant value

            if (selectedMachineSetup.machineType === 14)   // Saco RT
               _.assign(extProperties, { drivenConstant: 40, driverConstant: 21 }); // constant values

            // default values for certain machine types
            if (
               selectedMachineSetup.machineType === 30 ||  // Heli KV2008B
               selectedMachineSetup.machineType === 35     // Heli KV2019B
            )
               _.assign(extProperties, {
                  faultWarning: "false",
                  speedMonitoring: "false",
                  rotation: "S",
                  pulseRate: 0,
                  runReset: "false",
                  counterSwitch: "false",
               });

            if (selectedMachineSetup.machineType === 3)    // M50
               _.assign(extProperties, { gearDriven: "false" });

            if (selectedMachineSetup.machineType === 27)   // Mahan Oven
               _.assign(extProperties, {
                  oven1PanelBtm: "false",
                  oven1PanelTop: "false",
                  oven2PanelBtm: "false",
                  oven2PanelTop: "false",
                  creelType: "Standard",
                  numberPasses: "1",
               });

            if (
               selectedMachineSetup.machineType === 21 ||  // SSM DP5
               selectedMachineSetup.machineType === 31 ||  // SSM King TK2
               selectedMachineSetup.machineType === 38 ||  // SSM Duro
               selectedMachineSetup.machineType === 36     // SSM Xeno
            )
               _.assign(extProperties, { windType: "Digiwind" });

            if (selectedMachineSetup.machineType === 10)   // Saurer TC2
               _.assign(extProperties, { tensionDevice: "Flyer", potLids: "true" });

            if (selectedMachineSetup.machineType === 15)   // UT50
               _.assign(extProperties, {
                  tensionDevice: "Flyer",
                  potLids: "true",
                  reserveDisk: "160",
               });
         } else {
            extProperties = _.assign({}, selectedMachineSetup.extendedPropertyData || {});
            //only constant values for certain machine types
            if (selectedMachineSetup.machineType === 39)   // Mahan Dryer
               _.assign(extProperties, { pounds: 2000 });  // constant value

            if (selectedMachineSetup.machineType === 29)   // Heli KV2002
               _.assign(extProperties, { girth: 0.0503, type: 1 }); // constant values

            if (
               selectedMachineSetup.machineType === 16 ||  // M50 Wax
               selectedMachineSetup.machineType === 3      // M50
            )
               _.assign(extProperties, { scaleFactor: 0.09243 }); // constant value

            if (selectedMachineSetup.machineType === 14)   // Saco RT
               _.assign(extProperties, { drivenConstant: 40, driverConstant: 21 }); // constant values
         }

         if (!_.isEqual(DefaultErrors, errorDivDefinitions)) {
            setErrorDivDefinitions(DefaultErrors);
         }
         setMachineSetup({
            ...selectedMachineSetup,
            instructions: selectedMachineSetup.reset
               ? ""
               : selectedMachineSetup.instructions,
            extendedPropertyJson: extProperties,
            machineDescription: selectedMachineSetup.reset
               ? selectedMachineSetup.label
               : selectedMachineSetup.machineDescription,
         });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedMachineSetup]);

   useEffect(() => {
      if (_.isNil(selectedItem)) {
         // if(!firstRender.current) toast.info("Please Select another item that has a specification");
         setSpecification(null);
         return;
      }
      // this is being called every time a change in the machine is happening
      const fetchSpecification = () => {
         setIsLoading(true);
         api
            .post(`Quality/GetSpecificationByItemNumber`, {
               ItemNumber: selectedItem.value,
               IncludeMachineSetups: true,
            })
            .then((r) => {
               if (r.data?.success) {
                  let spec = r.data.item;
                  if (spec?.machineSetupList?.length <= 1) {
                     setCalcTypes([
                        constants.CALCULATION_TYPES.CUMULATIVE,
                        constants.CALCULATION_TYPES.SOLE_SETUP
                     ]);
                  } else {
                     setCalcTypes([
                        constants.CALCULATION_TYPES.ALTERNATE,
                        constants.CALCULATION_TYPES.CUMULATIVE,
                        constants.CALCULATION_TYPES.SOLE_SETUP,
                     ]);
                  }
                  setSpecification(spec);
                  setSelectedMachineSetup(null);
               } else {
                  setSpecification(null);
                  setCalcTypes([]);
               }
            })
            .catch(api.catchHandler)
            .finally(() => setIsLoading(false));
      };

      fetchSpecification();
   }, [selectedItem]);

   useEffect(() => {
      let apiCalls = [];
      apiCalls.push(
         api
            .fetch("Reference/GetMachineTypes")
            .then((response) => {
               return {
                  machineTypeList: _.chain(response.data?.list)
                     // .filter((m) => { return _.some(getMachineTypes(), (mt) => mt.value === m.id); })
                     .map((m) => {
                        m.value = m.id;
                        m.label = m.name;
                        return m;
                     })
                     .sortBy((m) => {
                        return m.label;
                     })
                     .value(),
               };
            })
            .catch(api.catchHandler)
      );
      apiCalls.push(
         api
            .fetch("Item/GetSpecItemsWithSamples", { searchString: "" })
            .then((response) => {
               return {
                  itemDropdownList: _.map(response.data.list, (d) => {
                     d.value = d.itemNumber;
                     // need all the values for other
                     return d;
                  }),
               };
            })
            .catch(api.catchHandler)
      );
      Promise.all(apiCalls)
         .then((arrayResults) => {
            let aggResults = {};
            _.each(arrayResults, (x) => Object.assign(aggResults, x));
            setMachineTypes(aggResults.machineTypeList ? aggResults.machineTypeList : []);
            if (
               aggResults.itemDropdownList &&
               aggResults.itemDropdownList.length > 0
            ) {
               const origList = aggResults.itemDropdownList;
               const shortHandLabelList = _.map(origList, (d) => {
                  let newItem = _.assign({}, d);
                  newItem.label = d.itemNumber;
                  return newItem;
               });
               setCopyToItemOptions(shortHandLabelList);
            }

            if (_.isNil(machineSetupSelect)) {
               return;
            } else {
               const json = decodeURI(machineSetupSelect);
               const opt = JSON.parse(json);
               setSelectedItem(opt);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.warning("There was an error loading the drop down list data");
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // memoize the full context values
   const contextUpdater = useMemo(() => ({
      handleItemSelectChange,
      onChange,
      setIsLoading,
      setSpecification,
      newHandleNewSetup,
      handldePrintActiveSetups,
      setPrintOptions,
      handlePrintMachineSetup,
      handleDeactivateMachineSetup,
      handleDeleteMachineSetup,
      handleCopyMachineItemSetup,
      setItemToDelete,
      setItemToCopy,
      setSelectedMachineSetup,
      onChangeErrorDivDefinitions,
      onChangeSelectedMachine,
      handleInputRangeChange,
      handleInputRangeChangeFloatIncremental,
      handleInputTimeChange,
      handleSaveMachineSetup,
   }),
      [
         handleItemSelectChange,
         onChange,
         setIsLoading,
         setSpecification,
         newHandleNewSetup,
         handldePrintActiveSetups,
         setPrintOptions,
         handlePrintMachineSetup,
         handleDeactivateMachineSetup,
         handleDeleteMachineSetup,
         handleCopyMachineItemSetup,
         setItemToDelete,
         setItemToCopy,
         setSelectedMachineSetup,
         onChangeErrorDivDefinitions,
         onChangeSelectedMachine,
         handleInputRangeChange,
         handleInputRangeChangeFloatIncremental,
         handleInputTimeChange,
         handleSaveMachineSetup,
      ]
   );

   const contextValue = useMemo(() => ({
      machineSetup,
      machineNums,
      selectedMachineSetup,
      selectedMachines,
      selectedItem,
      specification,
      copyToItemOptions,
      printOptions,
      itemToCopy,
      itemToDelete,
      machineTypes,
      MachineSetupDisplay,
      calcTypes,
      errorDivDefinitions,
   }), // eslint-disable-next-line react-hooks/exhaustive-deps
      [
         machineSetup,
         machineNums,
         selectedMachineSetup,
         selectedMachines,
         selectedItem,
         specification,
         copyToItemOptions,
         printOptions,
         itemToCopy,
         itemToDelete,
         machineTypes,
         calcTypes,
         errorDivDefinitions,
      ]
   );

   const contextLoading = useMemo(() => ({ isLoading, }),
      [isLoading]
   );

   return (
      // provider gives access to the context to all children
      <MachineSetupContextLoading.Provider value={contextLoading}>
         <MachineSetupContextState.Provider value={contextValue}>
            <MachineSetupContextUpdater.Provider value={contextUpdater}>
               {children}
            </MachineSetupContextUpdater.Provider>
         </MachineSetupContextState.Provider>
      </MachineSetupContextLoading.Provider>
   );
};

export {
   MachineSetupContextLoading,
   MachineSetupContextState,
   MachineSetupContextUpdater,
   MachineSetupContextProvider,
};
